<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'wasteBins',
    path: '/waste-bins',
    component: 'WasteBinsBrowser',
    title: () => gettext('Waste Bins'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WasteBinsBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'name',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Name'),
      formatter: {
        name: 'routeLink',
        to: 'fixedAsset',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'bin_capacity_liters',
      type: 'number',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Capacity (liters)'),
      formatter: 'number',
    },
    {
      field: 'waste_bin_name',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Bin Name'),
    },
    {
      field: 'worksheets',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Worksheets'),
    },
    {
      field: 'bin_type',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Bin Type'),
      formatter: {
        name: 'gettext',
        context: 'WasteBinTypes',
        conversion: 'StartCase',
      },
    },
    {
      field: 'rental_name',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Rental'),
      formatter: {
        name: 'routeLink',
        to: 'rental',
        navigateOnIconClick: true,
        params: {
          id: 'rental_id',
        },
      },
    },
    {
      field: 'rental_client_name',
      name: $fn.value.pgettext('WasteBinsBrowser', 'Tenant'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'rental_client_id',
        },
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('browse_waste_bins');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Waste Bins'),
      icon: $config.value.wasteBin.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_vmwgyl4q9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_f3e3sjwfn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_rathznfiv_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
      props: {
        assetType: 'waste_bin',
      },
    };
  };

  const act_7315092382398562_closeModal_vmwgyl4q9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_vmwgyl4q9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_8tnuhv7p0 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_vmwgyl4q9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_8tnuhv7p0],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_vl6rp2ify =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_tx5ujks6b =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_vl6rp2ify,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_tx5ujks6b],
    event: 'reload',
  });
  const act_2248226175642056_setData_f3e3sjwfn = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_f3e3sjwfn_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_1co4shhjw =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_f3e3sjwfn,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_1co4shhjw],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_rathznfiv =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_rathznfiv_getActionArgs,
    });
  const inst_evh_4615685607825208_click_aar9dpfek =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_rathznfiv,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_aar9dpfek],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_8tnuhv7p0,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_tx5ujks6b,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_1co4shhjw,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_aar9dpfek,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_8tnuhv7p0 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_8tnuhv7p0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_tx5ujks6b = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_tx5ujks6b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_1co4shhjw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_1co4shhjw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_aar9dpfek = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_aar9dpfek.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WasteBinsBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="wasteBins"
      :headerTitle="gettext('Waste bins')"
      :items="ds"
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'waste-bins')"
      checkbox-row-selection
      footer-totals
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.wasteBin.createWasteBinIcon"
            :text="pgettext('WasteBins', 'Create Waste Bin...')"
            size="sm"
            variant="light"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/assets/fixed-assets.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
